import { useTranslation } from 'react-i18next';
import i18n from '@/app/i18n';
import type { CatalogTranslations } from '@/types/menu';

type Field = 'name' | 'description';

/** SC-1278 — texte du catalogue dans la langue de l'interface. Le français
 *  (ou une langue sans saisie) retombe sur le texte de base, jamais vide. */
export function localizedText(
  base: string,
  translations: CatalogTranslations | undefined,
  field: Field,
  lang: string,
): string {
  const code = (lang || 'fr').toLowerCase().split('-')[0] as keyof CatalogTranslations;
  if (code === ('fr' as string)) return base;
  const value = translations?.[code]?.[field]?.trim();
  return value || base;
}

export interface Named {
  name?: string;
  label?: string;
  description?: string;
  translations?: CatalogTranslations;
}

/** Hors composant (messages d'erreur, devis refusé) : langue courante lue à
 *  l'appel — ces chaînes sont fabriquées au moment de l'affichage. */
export function catalogName(e: Named): string {
  return localizedText(e.name ?? e.label ?? '', e.translations, 'name', i18n.language);
}

/** Lit la langue courante de façon réactive : un changement de langue
 *  re-rend sans nouvel appel réseau. */
export function useCatalogText() {
  useTranslation(); // abonnement : un changement de langue re-rend l'appelant
  const lang = i18n.language;
  return {
    lang,
    /** `name` d'un plat/catégorie ou `label` d'un groupe/choix d'options. */
    name: (e: Named) => localizedText(e.name ?? e.label ?? '', e.translations, 'name', lang),
    description: (e: Named) =>
      localizedText(e.description ?? '', e.translations, 'description', lang),
    /** Texte de base fourni à part (ex. instantané de panier). */
    text: (base: string, translations: CatalogTranslations | undefined, field: Field = 'name') =>
      localizedText(base, translations, field, lang),
  };
}
